import React, { useEffect, useState, useMemo } from "react";
import { apiClient } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import {
  Sparkles, Plus, Edit3, Trash2, Power, ChevronDown, ChevronUp,
  Loader2, BarChart3, MessageCircleQuestion, TrendingUp, ThumbsUp,
  ThumbsDown, AlertCircle, X, Save, Search,
} from "lucide-react";

export default function FaqAdminTab() {
  const { lang } = useI18n();
  const [view, setView] = useState("articles"); // articles | analytics | escalations
  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [filterCat, setFilterCat] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null); // null | { ... } for create/edit
  const [analytics, setAnalytics] = useState(null);
  const [escalations, setEscalations] = useState([]);
  const [unanswered, setUnanswered] = useState([]);

  useEffect(() => {
    apiClient.get("/faq/categories").then(({ data }) => setCategories(data.items || []));
  }, []);

  const loadArticles = async () => {
    setLoading(true);
    const params = {};
    if (filterCat) params.category = filterCat;
    try {
      const { data } = await apiClient.get("/admin/faq", { params });
      setArticles(data.items || []);
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    const [s, u] = await Promise.all([
      apiClient.get("/admin/faq/analytics/summary"),
      apiClient.get("/admin/faq/analytics/unanswered"),
    ]);
    setAnalytics(s.data);
    setUnanswered(u.data.items || []);
  };

  const loadEscalations = async () => {
    const { data } = await apiClient.get("/admin/faq/escalations");
    setEscalations(data.items || []);
  };

  useEffect(() => {
    if (view === "articles") loadArticles();
    if (view === "analytics") loadAnalytics();
    if (view === "escalations") loadEscalations();
  }, [view, filterCat]);

  const filteredArticles = useMemo(() => {
    if (!q.trim()) return articles;
    const needle = q.toLowerCase();
    return articles.filter(a =>
      (a.question_ar || "").toLowerCase().includes(needle) ||
      (a.question_en || "").toLowerCase().includes(needle)
    );
  }, [articles, q]);

  const toggleEnable = async (a) => {
    await apiClient.patch(`/admin/faq/${a.id}/toggle`);
    setArticles(prev => prev.map(x => x.id === a.id ? { ...x, enabled: !x.enabled } : x));
  };

  const remove = async (a) => {
    if (!window.confirm(lang === "ar" ? "حذف هذا السؤال نهائياً؟" : "Delete this FAQ permanently?")) return;
    await apiClient.delete(`/admin/faq/${a.id}`);
    setArticles(prev => prev.filter(x => x.id !== a.id));
  };

  return (
    <div className="space-y-5" data-testid="admin-faq-tab">
      {/* Sub-tabs */}
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={() => setView("articles")} className={`px-4 py-2 rounded-full text-sm ${view === "articles" ? "bg-[#0a0a0a] text-white" : "bg-[#0a0a0a]/5"}`} data-testid="faq-view-articles">
          {lang === "ar" ? "إدارة الأسئلة" : "Articles"}
        </button>
        <button onClick={() => setView("analytics")} className={`px-4 py-2 rounded-full text-sm ${view === "analytics" ? "bg-[#0a0a0a] text-white" : "bg-[#0a0a0a]/5"}`} data-testid="faq-view-analytics">
          {lang === "ar" ? "التحليلات" : "Analytics"}
        </button>
        <button onClick={() => setView("escalations")} className={`px-4 py-2 rounded-full text-sm ${view === "escalations" ? "bg-[#0a0a0a] text-white" : "bg-[#0a0a0a]/5"}`} data-testid="faq-view-escalations">
          {lang === "ar" ? "الرسائل المُحوَّلة" : "Escalations"}
        </button>
      </div>

      {/* Articles management */}
      {view === "articles" && (
        <>
          <div className="surface rounded-xl p-4 flex items-center gap-3 flex-wrap">
            <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)} className="px-3 py-2 rounded-lg border border-[#0a0a0a]/15 text-sm bg-white" data-testid="faq-admin-cat-filter">
              <option value="">{lang === "ar" ? "كل الفئات" : "All categories"}</option>
              {categories.map(c => <option key={c.key} value={c.key}>{lang === "ar" ? c.ar : c.en}</option>)}
            </select>
            <div className="flex items-center gap-1 bg-white border border-[#0a0a0a]/15 rounded-lg px-3 py-2 flex-1 max-w-md">
              <Search className="w-4 h-4 text-[#8a8a8a]" />
              <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder={lang === "ar" ? "ابحث..." : "Search..."} className="flex-1 text-sm outline-none" data-testid="faq-admin-search" />
            </div>
            <button onClick={() => setEditor({ category: categories[0]?.key || "buying", question_ar: "", question_en: "", answer_ar: "", answer_en: "", order: 100, enabled: true })} className="btn-primary text-sm ms-auto" data-testid="faq-admin-create">
              <Plus className="w-4 h-4" />
              {lang === "ar" ? "إضافة سؤال" : "Add FAQ"}
            </button>
          </div>

          {loading ? (
            <div className="text-sm text-[#6b6b6b] inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> {lang === "ar" ? "جارٍ التحميل..." : "Loading..."}</div>
          ) : (
            <div className="space-y-2" data-testid="faq-admin-list">
              {filteredArticles.map(a => (
                <div key={a.id} className={`surface rounded-xl p-4 ${!a.enabled ? "opacity-60" : ""}`} data-testid={`faq-admin-row-${a.id}`}>
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] uppercase tracking-widest text-[#8a6f2e] font-semibold">{categories.find(c => c.key === a.category)?.[lang] || a.category}</span>
                        {!a.enabled && <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">{lang === "ar" ? "معطّل" : "Disabled"}</span>}
                      </div>
                      <div className="font-display text-base mt-1">{lang === "ar" ? a.question_ar : a.question_en}</div>
                      <div className="text-xs text-[#6b6b6b] mt-1 flex items-center gap-3">
                        <span><TrendingUp className="w-3 h-3 inline" /> {a.view_count || 0} {lang === "ar" ? "مشاهدة" : "views"}</span>
                        <span><ThumbsUp className="w-3 h-3 inline text-green-600" /> {a.helpful_yes || 0}</span>
                        <span><ThumbsDown className="w-3 h-3 inline text-rose-600" /> {a.helpful_no || 0}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => toggleEnable(a)} className="w-9 h-9 rounded-lg hover:bg-black/5 flex items-center justify-center" title={a.enabled ? "Disable" : "Enable"} data-testid={`faq-admin-toggle-${a.id}`}>
                        <Power className={`w-4 h-4 ${a.enabled ? "text-green-600" : "text-rose-600"}`} />
                      </button>
                      <button onClick={() => setEditor(a)} className="w-9 h-9 rounded-lg hover:bg-black/5 flex items-center justify-center" data-testid={`faq-admin-edit-${a.id}`}>
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button onClick={() => remove(a)} className="w-9 h-9 rounded-lg hover:bg-rose-50 flex items-center justify-center" data-testid={`faq-admin-delete-${a.id}`}>
                        <Trash2 className="w-4 h-4 text-rose-600" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {filteredArticles.length === 0 && (
                <div className="text-sm text-[#6b6b6b] py-6 text-center">{lang === "ar" ? "لا توجد نتائج" : "No results"}</div>
              )}
            </div>
          )}
        </>
      )}

      {/* Analytics */}
      {view === "analytics" && analytics && (
        <div className="space-y-5" data-testid="faq-analytics-view">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label={lang === "ar" ? "إجمالي الأسئلة" : "Total FAQs"} value={analytics.totals.articles} />
            <Stat label={lang === "ar" ? "المفعّلة" : "Enabled"} value={analytics.totals.enabled} />
            <Stat label={lang === "ar" ? "إجمالي المشاهدات" : "Total views"} value={analytics.totals.views} />
            <Stat label={lang === "ar" ? "نسبة المفيد" : "Helpful %"} value={`${analytics.totals.helpful_pct}%`} accent />
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <div className="surface rounded-xl p-5">
              <h3 className="font-display text-lg mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-[#8a6f2e]" /> {lang === "ar" ? "الأكثر مشاهدة" : "Most viewed"}</h3>
              <ul className="space-y-2 text-sm" data-testid="faq-top-viewed">
                {analytics.top_viewed.map((a) => (
                  <li key={a.id} className="flex items-center justify-between gap-3">
                    <span className="truncate">{lang === "ar" ? a.question_ar : a.question_en}</span>
                    <span className="text-[#8a6f2e] font-display">{a.view_count}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="surface rounded-xl p-5">
              <h3 className="font-display text-lg mb-3 flex items-center gap-2"><Search className="w-4 h-4 text-[#8a6f2e]" /> {lang === "ar" ? "أعلى كلمات البحث" : "Top searches"}</h3>
              <ul className="space-y-2 text-sm" data-testid="faq-top-searches">
                {analytics.top_searches.map((s) => (
                  <li key={s.query} className="flex items-center justify-between gap-3">
                    <span className="truncate">{s.query}</span>
                    <span className="text-[#8a6f2e] font-display">{s.count}</span>
                  </li>
                ))}
                {analytics.top_searches.length === 0 && <li className="text-[#6b6b6b] text-xs">{lang === "ar" ? "لا توجد بيانات" : "No data"}</li>}
              </ul>
            </div>
          </div>

          {unanswered.length > 0 && (
            <div className="surface rounded-xl p-5">
              <h3 className="font-display text-lg mb-3 flex items-center gap-2"><AlertCircle className="w-4 h-4 text-rose-600" /> {lang === "ar" ? "بحوث بدون نتائج (مرشحون لإجابات جديدة)" : "Unanswered searches (good candidates for new FAQs)"}</h3>
              <ul className="space-y-1 text-sm" data-testid="faq-unanswered">
                {unanswered.slice(0, 10).map((u) => (
                  <li key={u.query} className="flex items-center justify-between gap-3">
                    <span className="truncate">{u.query}</span>
                    <span className="text-rose-600 font-display text-xs">{u.count}×</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Escalations */}
      {view === "escalations" && (
        <div className="surface rounded-xl p-5" data-testid="faq-escalations-view">
          <h3 className="font-display text-lg mb-4">{lang === "ar" ? "الرسائل المُحوَّلة من مركز المساعدة" : "Messages escalated from Help Center"}</h3>
          {escalations.length === 0 ? (
            <div className="text-sm text-[#6b6b6b] py-4 text-center">{lang === "ar" ? "لا توجد رسائل" : "No messages"}</div>
          ) : (
            <div className="space-y-2 text-sm">
              {escalations.map(e => (
                <div key={e.id} className="border border-[#0a0a0a]/8 rounded-lg p-3" data-testid={`escalation-row-${e.id}`}>
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${e.type === "complaint" ? "bg-rose-100 text-rose-700" : e.type === "suggestion" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`}>{e.type}</span>
                    <span className="text-xs text-[#6b6b6b]">{new Date(e.created_at).toLocaleString(lang === "ar" ? "ar-SA" : "en-US")}</span>
                    {e.source === "ai_assistant" && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#c9a55c]/15 text-[#8a6f2e]">{lang === "ar" ? "من المساعد الذكي" : "From AI assistant"}</span>}
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${e.status === "new" ? "bg-amber-100 text-amber-700" : "bg-[#0a0a0a]/5"}`}>{e.status}</span>
                  </div>
                  <div className="font-semibold">{e.subject}</div>
                  <div className="text-[#3a3a3a] mt-1">{e.message}</div>
                  {e.user_email && <div className="text-xs text-[#8a8a8a] mt-1">📧 {e.user_email}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {editor && (
        <FaqEditor
          lang={lang}
          categories={categories}
          initial={editor}
          onClose={() => setEditor(null)}
          onSaved={(saved) => {
            setEditor(null);
            // Refresh list
            loadArticles();
          }}
        />
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`surface rounded-xl p-4 ${accent ? "bg-[#c9a55c]/10" : ""}`}>
      <div className="text-xs text-[#6b6b6b] uppercase tracking-widest">{label}</div>
      <div className="font-display text-2xl mt-1 text-[#0a0a0a]">{value}</div>
    </div>
  );
}

function FaqEditor({ lang, categories, initial, onClose, onSaved }) {
  const isNew = !initial.id;
  const [form, setForm] = useState({
    category: initial.category,
    question_ar: initial.question_ar || "",
    question_en: initial.question_en || "",
    answer_ar: initial.answer_ar || "",
    answer_en: initial.answer_en || "",
    order: initial.order || 100,
    enabled: initial.enabled !== false,
    related_ids: initial.related_ids || [],
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const save = async (e) => {
    e.preventDefault();
    if (!form.question_ar.trim() || !form.question_en.trim() || !form.answer_ar.trim() || !form.answer_en.trim()) {
      setErr(lang === "ar" ? "جميع الحقول الأربعة (سؤال/جواب بالعربي والإنجليزي) مطلوبة" : "All four fields required");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      if (isNew) await apiClient.post("/admin/faq", form);
      else await apiClient.patch(`/admin/faq/${initial.id}`, form);
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="faq-editor-modal">
      <div className="bg-white rounded-3xl w-full max-w-2xl p-6 shadow-2xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-2xl">{isNew ? (lang === "ar" ? "إضافة سؤال جديد" : "Add FAQ") : (lang === "ar" ? "تعديل السؤال" : "Edit FAQ")}</h3>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-black/5 flex items-center justify-center"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={save} className="space-y-3" data-testid="faq-editor-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold mb-1 block">{lang === "ar" ? "الفئة" : "Category"}</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full px-3 py-2 border-2 border-black/10 rounded-lg text-sm" data-testid="faq-editor-category">
                {categories.map(c => <option key={c.key} value={c.key}>{lang === "ar" ? c.ar : c.en}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold mb-1 block">{lang === "ar" ? "ترتيب العرض" : "Display order"}</label>
              <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) || 100 })} className="w-full px-3 py-2 border-2 border-black/10 rounded-lg text-sm" data-testid="faq-editor-order" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold mb-1 block">السؤال (عربي)</label>
            <input type="text" value={form.question_ar} onChange={(e) => setForm({ ...form, question_ar: e.target.value })} className="w-full px-3 py-2 border-2 border-black/10 rounded-lg text-sm" dir="rtl" required data-testid="faq-editor-q-ar" />
          </div>
          <div>
            <label className="text-xs font-semibold mb-1 block">Question (English)</label>
            <input type="text" value={form.question_en} onChange={(e) => setForm({ ...form, question_en: e.target.value })} className="w-full px-3 py-2 border-2 border-black/10 rounded-lg text-sm" dir="ltr" required data-testid="faq-editor-q-en" />
          </div>
          <div>
            <label className="text-xs font-semibold mb-1 block">الإجابة (عربي)</label>
            <textarea value={form.answer_ar} onChange={(e) => setForm({ ...form, answer_ar: e.target.value })} rows={4} className="w-full px-3 py-2 border-2 border-black/10 rounded-lg text-sm" dir="rtl" required data-testid="faq-editor-a-ar" />
          </div>
          <div>
            <label className="text-xs font-semibold mb-1 block">Answer (English)</label>
            <textarea value={form.answer_en} onChange={(e) => setForm({ ...form, answer_en: e.target.value })} rows={4} className="w-full px-3 py-2 border-2 border-black/10 rounded-lg text-sm" dir="ltr" required data-testid="faq-editor-a-en" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} data-testid="faq-editor-enabled" />
            {lang === "ar" ? "تفعيل هذا السؤال" : "Enable this FAQ"}
          </label>
          {err && <div className="text-sm text-rose-600">{err}</div>}
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="btn-ghost flex-1 justify-center">{lang === "ar" ? "إلغاء" : "Cancel"}</button>
            <button type="submit" disabled={busy} className="btn-primary flex-1 justify-center" data-testid="faq-editor-save">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {lang === "ar" ? "حفظ" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
